import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useClubs } from '../../../hooks/clubs/useClubs'
import { usePriceRules } from '../../../hooks/pricing/usePriceRules'
import { useCourtOptions } from '../../../hooks/court/useCourtOptions'

import { Card } from '../../../components/card/Card'
import { Button } from '../../../components/buttons/Button'
import Loading from '../../../components/loading/Loading'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { PriceRuleForm, emptyPriceRule } from '../../../components/authenticated/management/pricing/PriceRuleForm'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { PriceRule } from '../../../types/booking/booking'
import { ClubModelMetadata } from '../../../types/court/court'

export const ManagerPriceRule = () => {
  const { priceRuleId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { t } = useTranslation('pricing')
  const clubsHook = useClubs()
  const { versions, create, update, loading } = usePriceRules()
  const { courts, loaded: courtsLoaded } = useCourtOptions()
  const [clubs, setClubs] = useState<ClubModelMetadata[] | null>(null)
  const [initial, setInitial] = useState<PriceRule | null>(null)
  const isEditMode = priceRuleId !== undefined
  const listPath = (clubId?: number) => `/m/pricing${clubId ? `?clubId=${clubId}` : ''}`

  useEffect(() => {
    clubsHook.clubs(50).then(page => setClubs(page?.content ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!clubs || !courtsLoaded) {
      return
    }
    if (!isEditMode) {
      const clubId = Number(searchParams.get('clubId'))
      setInitial(emptyPriceRule(clubs.find(club => club.id === clubId) ?? clubs[0], courts))
      return
    }
    // Only this rule's versions are needed to find the one being edited.
    versions(Number(priceRuleId))
      .then(rules => {
        const rule = rules?.find(item => String(item.id) === priceRuleId)
        if (!rule) {
          navigate(listPath(), { replace: true })
          return
        }
        setInitial(rule)
      })
      .catch(caught => {
        // An unknown id answers 404: show why and go back to the list, as for a missing rule.
        showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message })
        navigate(listPath(), { replace: true })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubs, courtsLoaded, priceRuleId])

  const handleSave = async (rule: PriceRule) => {
    try {
      if (isEditMode) {
        await update(Number(priceRuleId), rule)
        showBanner({ kind: BannerKind.OK, msg: t('pricing.successfully-updated') })
      } else {
        await create(rule)
        showBanner({ kind: BannerKind.OK, msg: t('pricing.successfully-created') })
      }
      navigate(listPath(rule.clubId), { replace: true })
    } catch (caught) {
      showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message })
    }
  }

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t('pricing.manager.eyebrow'),
          h1: isEditMode ? initial?.name ?? t('pricing.form.edit-title') : t('pricing.form.new-title'),
        }}
        sub={t('pricing.manager.form-sub')}
        actions={[
          <Button
            key="back"
            label={t('pricing.form.back-btn')}
            size="md"
            variant="ghost"
            onClick={() => navigate(listPath(initial?.clubId))}
          />,
        ]}
      />
      {initial && clubs ? (
        <Card className="col-12">
          <PriceRuleForm
            key={initial.id ?? `new-${initial.clubId}`}
            initial={initial}
            clubs={clubs}
            courts={courts}
            isEditMode={isEditMode}
            loading={loading}
            onSave={handleSave}
            onCancel={() => navigate(listPath(initial.clubId))}
          />
        </Card>
      ) : (
        <Loading variant="inline" />
      )}
    </>
  )
}
